import { useState } from "react";
import { useAsyncData } from "../../../hooks/useAsyncData";
import { useAsyncHandler } from "../../../hooks/useAsyncHandler";
import { checkDocumentAdmission, getDocumentExtractionTrace } from "../../../lib/corpusApi";
import type {
  AdmissionCheckResponse,
  DocumentExtractionTraceResponse,
} from "../../../lib/corpusApi";

interface UseDocumentExtractionTraceResult {
  data: DocumentExtractionTraceResponse;
  /** True once the lookup has finished and found no trace row — distinct from still loading, so
   *  the page can tell "searching" from "searched, found nothing" (e.g. a document that predates
   *  migration 031). */
  notFound: boolean;
  isLoading: boolean;
  error: string | null;
  recheck: {
    run: () => Promise<void>;
    loading: boolean;
    error: string | null;
    /** The gate's verdict from the last re-check on this page; null before the first one. */
    result: AdmissionCheckResponse | null;
  };
}

/** Loads one document's extraction trace, and re-runs its admission check, which reloads the
 *  trace so the page shows the fresh result. Mount it fresh per document. */
export function useDocumentExtractionTrace(documentId: string): UseDocumentExtractionTraceResult {
  const trace = useAsyncData(() => getDocumentExtractionTrace(documentId));
  const [recheckResult, setRecheckResult] = useState<AdmissionCheckResponse | null>(null);

  const recheck = useAsyncHandler(async () => {
    setRecheckResult(await checkDocumentAdmission(documentId));
    await trace.refresh();
  });

  return {
    data: trace.data,
    notFound: !trace.loading && !trace.error && trace.data === null,
    isLoading: trace.loading,
    error: trace.error,
    recheck: {
      run: recheck.handle,
      loading: recheck.loading,
      error: recheck.error,
      result: recheckResult,
    },
  };
}
